import { Close, Menu } from '@mui/icons-material';
import {
  AppBar, Box, Button, Container,
  IconButton, Stack, Theme,
  Toolbar,
  useTheme
} from '@mui/material';
import { CrossDomainLink } from '@seeka-labs/converge-react';
import { Link as GatsbyLink, graphql, useStaticQuery } from 'gatsby';
import React from 'react';

import { getPartnerLink } from '../shared/hooks/usePartnerCodeLink';
import { Link } from './link';
import { LinkGroupProps } from './linkGroup';
import { LinkList } from './linkList';
import { ContentfulAssetImage } from './ui';


export const mobileNavLinkStyle = (muiTheme: Theme) => ({
  display: "block",
  color: "white",
  zIndex: 1,
  fontSize: muiTheme.typography.h3.fontSize,
  paddingTop: muiTheme.spacing(1),
  paddingBottom: muiTheme.spacing(2),
  paddingLeft: muiTheme.spacing(4),
  paddingRight: muiTheme.spacing(4),
})

export const mobileAppFloatingMenuStyle = (muiTheme: Theme) => ({
  color: "white",
  zIndex: 999,
  // fontSize: muiTheme.typography.h3.fontSize,
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  pt: 7,
  pl: 3,
  pr: 3,
  position: "absolute",
  background: "linear-gradient(180deg, #1E0C42 50%, #6C60C0 200%)",
})

export const desktopAppBarStyle = (theme: Theme) => ({
  background: "transparent",
  pt: 2,
  display: {
    xs: "none",
    sm: "none",
    md: "none",
    lg: "inherit",
    xl: "inherit",
  },
  // width: "100%",
  // top: 0,
  // left: 0,
  // right: 0,
})

export const mobileAppBarStyle = (theme: Theme) => ({
  background: "transparent",
  pt: 1,
  pr: 1,
  pl: 1,
  display: {
    xs: "inherit",
    sm: "inherit",
    md: "inherit",
    lg: "none",
    xl: "none",
  },
  // width: "100%",
  // top: 0,
  // left: 0,
  // right: 0,
})

export type HeaderProps = {
  darkForeground?: boolean
  location: Location
}
export const baseLogoHeight = 47
export const baseLogoWidth = 150

export default function Header({ darkForeground, location }: HeaderProps) {
  const data: Queries.HeaderComponentQuery = useStaticQuery(graphql`
    query HeaderComponent {
      layoutHeader: contentfulLayoutHeader {
        id
        logo {
          url
          id
          title      
          __typename
          contentful_id
          file {
            contentType
          }      
          localFile {
            childImageSharp { 
              gatsbyImageData(placeholder: NONE, width: 150, height: 47)
            }
          }
        }
        navItems {
          ... on ContentfulNavItem {
            id
            url: href
            label: text
            description
          }
          ... on ContentfulNavItemGroup {
            id
            label: name
            links: navItems {
              id
              description
              url: href
              label: text
              dropdownItemIcon: icon {
                url
                id
                title      
                __typename
                contentful_id
                file {
                  contentType
                }      
                localFile {
                  childImageSharp { 
                    gatsbyImageData(placeholder: NONE, width: 40, layout: FIXED)
                  }
                }
              }
            }
          }
        }
        cta {
          id
          href
          text
        }
      }
    }
  `)

  return (
    <>
      <MobileHeader darkForeground={darkForeground} location={location} {...data} />
      <DesktopHeader darkForeground={darkForeground} location={location} {...data} />
    </>
  )
}

type HeaderComponentProps = Queries.HeaderComponentQuery & {
  darkForeground: boolean
  location: Location
}

const DesktopHeader = ({ darkForeground, location, ...props }: HeaderComponentProps) => {
  const { navItems, cta, logo } = props.layoutHeader
  const theme = useTheme()

  const appBarStyle = desktopAppBarStyle(theme)

  return (
    <AppBar position="absolute" elevation={0} sx={appBarStyle}>
      <Toolbar>
        <Container>
          <Stack
            direction={"row"}
            justifyContent="space-between"
            alignItems="center"
          >
            <Link url="/" forceLink relativeLinkComponent={GatsbyLink} sx={{ display: 'inline-flex' }}>
              <Box sx={{ display: "none" }}>Home</Box>
              <ContentfulAssetImage
                asset={logo}
                loading="eager"
                imgStyle={{
                  width: baseLogoWidth + "px",
                  height: baseLogoHeight + "px",
                }}
              />
            </Link>
            <nav>
              <Stack
                direction={"row"}
                justifyContent="space-between"
                alignItems="center"
                gap={{ md: 2, lg: 4 }}
              >
                {navItems && navItems.length > 0 ? (
                  <LinkList
                    links={navItems as any as LinkGroupProps[]}
                    enableIcons
                    withDropdown
                    disableGroupDropDownIcon
                    StackProps={{ direction: "row", gap: { md: 1, lg: 2 } }}
                    LinkDropdownPaperProps={{ sx: { backgroundColor: 'rgb(255,255,255)' } }}
                    relativeLinkComponent={GatsbyLink}
                    LinkProps={{
                      sx: {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      }
                    }}
                    LinkGroupHeaderLinkProps={{
                      sx: {
                        color: 'white',
                        fontWeight: 'normal',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                ) : <></>}

                <div>
                  {cta && (
                    <CrossDomainLink>
                      <Button
                        href={getPartnerLink(cta.href, location)}
                        variant="contained"
                        target="_blank"
                        disableElevation
                        sx={
                          !darkForeground
                            ? {
                              color: theme.palette.primary.main,
                              borderColor: "white",
                              background: "white",
                              "&:hover": {
                                backgroundColor: theme.palette.grey[400],
                              },
                            }
                            : {}
                        }
                      >
                        {cta.text}
                      </Button>
                    </CrossDomainLink>
                  )}
                </div>
              </Stack>
            </nav>
          </Stack>
        </Container>
      </Toolbar>
    </AppBar>
  )
}

const MobileHeader = ({ darkForeground, location, ...props }: HeaderComponentProps) => {
  const { navItems, cta, logo } = props.layoutHeader

  const theme = useTheme()

  const appBarStyle = mobileAppBarStyle(theme)
  const openMenuStyle = mobileAppFloatingMenuStyle(theme)
  const [isOpen, setOpen] = React.useState(false)

  React.useEffect(() => {
    const heroScroller = document.getElementById('hero-scroller')
    if (isOpen) {
      document.body.style.overflow = "hidden"
      document.documentElement.style.overflow = "hidden"
      if (heroScroller) {
        heroScroller.style.display = 'none'
      }
    } else {
      document.body.style.overflow = ""
      document.documentElement.style.overflow = ""
      if (heroScroller) {
        heroScroller.style.display = ''
      }
    }
  }, [isOpen])

  return (
    <>
      <AppBar position="absolute" elevation={0} sx={appBarStyle}>
        <Toolbar sx={{ p: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: "100%" }}
            justifyContent={"space-between"}
          >
            <Link url="/" forceLink onClick={() => setOpen(false)} relativeLinkComponent={GatsbyLink} sx={{ display: 'inline-flex' }}>
              <Box sx={{ display: "none" }}>Home</Box>
              <Box
                sx={{
                  height: {
                    xs: baseLogoHeight * 0.6 + "px",
                    sm: baseLogoHeight + "px",
                  },
                  width: {
                    xs: baseLogoWidth * 0.6 + "px",
                    sm: baseLogoWidth + "px",
                  },
                }}
              >
                <ContentfulAssetImage
                  asset={logo}
                  loading="eager"
                  objectFit="cover"
                />
              </Box>
            </Link>

            <Stack
              direction="row"
              alignItems="center"
              gap={2}
              justifyContent={"flex-end"}
            >
              {!isOpen && cta && (
                <CrossDomainLink>
                  <Button
                    href={getPartnerLink(cta.href, location)}
                    size="small"
                    target="_blank"
                    variant={isOpen ? "outlined" : "contained"}
                    sx={{
                      color: "white",
                      borderColor: "white",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      p: { xs: "7px 12px", sm: "10px 16px" },
                      // marginRight: isOpen ? "18px" : null,
                    }}
                  >
                    {cta.text}
                  </Button>
                </CrossDomainLink>
              )}
              <div>
                <IconButton
                  title="Toggle menu"
                  onClick={() => setOpen(!isOpen)}
                >
                  {isOpen ? (
                    <Close sx={{ color: "white" }} />
                  ) : (
                    <Menu sx={{ color: "white" }} />
                  )}
                </IconButton>
              </div>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>
      {isOpen && (
        <Box sx={openMenuStyle}>
          <nav>
            <Stack direction={'column'} sx={{ width: '100%' }} alignItems={'stretch'} gap={3}>
              {cta && (
                <Button
                  href={cta.href}
                  size="small"
                  target="_blank"
                  variant={"contained"}
                  sx={{
                    color: "white",
                    borderColor: "white",
                    fontSize: "1rem",
                    p: "10px 16px",
                    mt: "20px",
                    ml: "20px",
                    mr: "25px",
                    // marginRight: isOpen ? "18px" : null,
                  }}
                >
                  {cta.text}
                </Button>
              )}

              {navItems && (
                <LinkList
                  links={navItems as any as LinkGroupProps[]}
                  alignLeft
                  relativeLinkComponent={GatsbyLink}
                  StackProps={{ sx: { color: 'white' } }}
                  onClick={() => setOpen(false)}
                  LinkProps={{
                    sx: {
                      color: 'white', ml: 0
                    },
                  }}
                  LinkGroupHeaderLinkProps={{
                    sx: {
                      color: 'white', ml: 0
                    }
                  }}
                  LinkGroupHeaderTypographyProps={{
                    sx: {
                      color: 'white', ml: 0
                    }
                  }}
                  LinkGroupItemsTypographyProps={{
                    sx: {
                      color: 'white', ml: 0
                    }
                  }}
                />
              )}
            </Stack>
          </nav>
        </Box>
      )}
    </>
  )
}
